import { useEffect, useMemo, useState } from 'react';
import { Alert, Chip, Stack, Switch, Container, Button, Typography, Box, Paper, Snackbar, Collapse, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { addEmployee, addEmployeeWorkHours, createAndStoreEmployeeSignUpInvite, fetchAllEmployees, fetchServices, generateRecurringBreakBlockersForEmployee, getCurrentBusinessConfig, updateEmployee, updateEmployeeScheduleForDate } from '../../firebase/firebase-delegate';
import { BitStringData, Employee, RecurringBreak, Service, WorkHours } from '../../types';
import ErrorDialog from '../../components/global/ErrorDialog';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { TextField } from '@mui/material';
import { customAlphabet } from 'nanoid';
import { extractTimesFromBitString, generateScheduleBitString } from '../../time/timeUtils';
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK, DAYS_OF_WEEK_PREFERRED_ORDER, EMPLOYEE_BIO_MAX_LENGTH } from '../../constants';
import { dayjs } from '../../utils/dayjs-config';
import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import ScheduleCard from '../../components/employee/services/ScheduleCard';
import { hasDuplicateEmployeeEmail, normalizeEmail } from '../../utils/emailUtils';
import { convertBusinessHoursToWorkHours } from '../../utils/businessHours';
import ImageSelect from '../../components/global/images/ImageSelector';
import { tenantStoragePath } from '../../tenancy/paths';
import { useOptionalTenant } from '../../tenancy/TenantContext';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import { QuestionCard } from './Onboarding/OnboardingPrimitives';

type AddEmployeePageProps = {
  tenantIdOverride?: string;
  successPath?: string;
};

const parseFullName = (value: string): { firstName: string; lastName: string } => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const AddEmployeePage = ({ tenantIdOverride, successPath }: AddEmployeePageProps = {}) => {
  const tenant = useOptionalTenant();
  const tenantId = tenantIdOverride ?? tenant?.tenantId ?? '';
  const createStaffId = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', 20);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [inviteLink, setInviteLink] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>(undefined);
  const [imageSelectResetKey, setImageSelectResetKey] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [openCreateSuccessDialog, setOpenCreateSuccessDialog] = useState(false);
  const [createdEmployeeName, setCreatedEmployeeName] = useState('');

  const servicesQuery = useQuery({
    queryKey: ['services', tenantId],
    queryFn: () => fetchServices(tenantId),
    enabled: !!tenantId,
  });
  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig', tenantId],
    queryFn: () => getCurrentBusinessConfig(tenantId),
    enabled: !!tenantId,
  });
  const employeesQuery = useQuery({
    queryKey: ['allEmployees', tenantId],
    queryFn: () => fetchAllEmployees(tenantId),
    enabled: !!tenantId,
  });

  const isLoading = servicesQuery.isLoading || businessConfigQuery.isLoading || employeesQuery.isLoading;
  const isError = servicesQuery.isError || businessConfigQuery.isError || employeesQuery.isError;


  const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data);
  const showEmployeeBio = bookingUxConfig.uiFlags.showEmployeeBio;
  const showEmployeeProfilePhoto = bookingUxConfig.uiFlags.showEmployeeProfilePhoto;
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const employeeLimit = businessConfigQuery.data?.features?.employeeCount || 1;
  const loginlessEmployeeLimit = businessConfigQuery.data?.features?.loginlessEmployeeCount ?? employeeLimit;
  const isOrchestratorMode = businessConfigQuery.data?.preferences?.orchestratorMode === true ||
    loginlessEmployeeLimit > employeeLimit;
  const employees = employeesQuery.data ?? [];
  const currentLoginEnabledEmployeeCount = employees.filter((employee) => employee.isOrchestrated !== true).length;
  const currentLoginlessEmployeeCount = employees.filter((employee) => employee.isOrchestrated === true).length;
  const isAtLoginEnabledLimit = !!employeeLimit && currentLoginEnabledEmployeeCount >= employeeLimit;
  const isAtLoginlessLimit = !!loginlessEmployeeLimit && currentLoginlessEmployeeCount >= loginlessEmployeeLimit;
  const isAtLimit = isOrchestratorMode ? isAtLoginlessLimit : isAtLoginEnabledLimit;
  const fullNameValue = `${firstName} ${lastName}`.trim();

  const [currentStep, setCurrentStep] = useState(0); // Step starts at 0
  const steps = ['1', '2', '3']; // Numbered steps

  const createInviteMutation = useMutation({
    mutationFn: async () => {
      return await createAndStoreEmployeeSignUpInvite(isAdmin, selectedServices, tenantId);
    },
    onSuccess: (link) => setInviteLink(link),
    onError: (error) => {
      console.error('Error creating invite:', error);
      setShowErrorDialog(true)
    }
  });

  const isInviteLoading = createInviteMutation.isPending;
  const defaultWorkHours = useMemo(
    () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
    [businessConfigQuery.data?.businessInfo?.businessHours]
  );
  const [schedules, setSchedules] = useState<WorkHours>(defaultWorkHours);
  const [didHydrateBusinessHourDefaults, setDidHydrateBusinessHourDefaults] = useState(false);
  const [recurringBreakTemplates, setRecurringBreakTemplates] = useState<Record<keyof WorkHours, RecurringBreak[]>>(() =>
    DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {} as Record<keyof WorkHours, RecurringBreak[]>)
  );
  const templateEndDate = dayjs().add(3, 'month');

  useEffect(() => {
    if (didHydrateBusinessHourDefaults) {
      return;
    }
    if (!businessConfigQuery.data?.businessInfo?.businessHours) {
      return;
    }
    setSchedules(defaultWorkHours);
    setDidHydrateBusinessHourDefaults(true);
  }, [businessConfigQuery.data?.businessInfo?.businessHours, defaultWorkHours, didHydrateBusinessHourDefaults]);

  const flattenRecurringBreaks = () =>
    DAYJS_DAYS_OF_WEEK.flatMap((day, index) =>
      (recurringBreakTemplates[day] || []).map((definition) => ({
        ...definition,
        weekday: index,
      }))
    );

  const createLoginlessEmployeeMutation = useMutation({
    mutationFn: async () => {
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const trimmedEmail = normalizeEmail(email);
      const trimmedPhone = isResourceMode ? '' : phone.trim();
      const trimmedBio = showEmployeeBio ? bio.trim().slice(0, EMPLOYEE_BIO_MAX_LENGTH) : '';
      const loginlessEmployeeLimit = businessConfigQuery.data?.features?.loginlessEmployeeCount
        ?? businessConfigQuery.data?.features?.employeeCount
        ?? 1;
      const latestEmployees = await fetchAllEmployees(tenantId);
      const loginlessEmployees = latestEmployees.filter((employee) => employee.isOrchestrated === true);

      if (!trimmedFirstName) {
        throw new Error('First name is required.');
      }

      if (!!loginlessEmployeeLimit && loginlessEmployees.length >= loginlessEmployeeLimit) {
        throw new Error('Loginless employee limit exceeded');
      }

      if (!isResourceMode) {
        const duplicateEmail = hasDuplicateEmployeeEmail(latestEmployees, trimmedEmail);
        if (duplicateEmail) {
          throw new Error('An employee already exists with this email.');
        }
      }

      const employeeId = createStaffId();
      const profilePictureImagePath = (showEmployeeProfilePhoto && profilePictureFile)
        ? tenantStoragePath(tenantId, `employees/${employeeId}/profilePicture`)
        : undefined;
      const employee: Employee = {
        id: employeeId,
        admin: false,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: isResourceMode ? '' : trimmedEmail,
        phone: trimmedPhone,
        bio: trimmedBio || undefined,
        profilePictureImagePath,
        appointments: {},
        isOrchestrated: true,
        active: isActive,
        recurringBreaks: flattenRecurringBreaks(),
      };

      await addEmployee(employee, tenantId);
      if (showEmployeeProfilePhoto && profilePictureFile) {
        await updateEmployee(employee, employee, profilePictureFile, tenantId);
      }
      await addEmployeeWorkHours(employeeId, schedules, tenantId);

      const scheduleDates = Array.from({ length: BUFFER_PERIOD * 2 + 1 }, (_, index) => dayjs().add(index, 'day'));
      await Promise.all(
        scheduleDates.map((date) => updateEmployeeScheduleForDate(employeeId, schedules, date, tenantId))
      );

      const windowStart = dayjs();
      const windowEnd = dayjs().add(BUFFER_PERIOD * 2, 'day');
      await generateRecurringBreakBlockersForEmployee({
        employeeId,
        windowStart,
        windowEnd,
        recurringBreakIdsToReset: flattenRecurringBreaks().map((definition) => definition.id),
        siteId: tenantId,
      });

      if (selectedServices.length > 0) {
        await Promise.all(
          selectedServices.map((serviceId) =>
            updateDoc(
              doc(db, 'booking-websites', tenantId, 'services', serviceId),
              { employees: arrayUnion(employeeId) }
            )
          )
        );
      }

      return { createdEmployee: employee, assignedServiceIds: [...selectedServices] };
    },
    onSuccess: ({ createdEmployee, assignedServiceIds }) => {
      setCreatedEmployeeName(`${createdEmployee.firstName} ${createdEmployee.lastName}`.trim());
      setOpenCreateSuccessDialog(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setBio('');
      setProfilePictureFile(undefined);
      setImageSelectResetKey((previous) => previous + 1);
      setSelectedServices([]);
      setIsAdmin(false);
      setIsActive(true);
      setShowScheduleEditor(false);
      setSchedules(defaultWorkHours);
      setRecurringBreakTemplates(
        DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
          acc[day] = [];
          return acc;
        }, {} as Record<keyof WorkHours, RecurringBreak[]>)
      );
      queryClient.setQueryData<Employee[]>(['allEmployees', tenantId], (previous = []) => {
        if (previous.some((employee) => employee.id === createdEmployee.id)) {
          return previous;
        }
        return [...previous, createdEmployee];
      });
      queryClient.setQueryData<Employee[]>(['employees', tenantId], (previous = []) => {
        if (previous.some((employee) => employee.id === createdEmployee.id)) {
          return previous;
        }
        return [...previous, createdEmployee];
      });
      if (assignedServiceIds.length > 0) {
        queryClient.setQueryData<Service[]>(['services', tenantId], (previous = []) => (
          previous.map((service) => {
            if (!assignedServiceIds.includes(service.id)) {
              return service;
            }
            const employeeSet = new Set(service.employees ?? []);
            employeeSet.add(createdEmployee.id);
            return {
              ...service,
              employees: Array.from(employeeSet),
            };
          })
        ));
      }
      queryClient.invalidateQueries({ queryKey: ['allEmployees', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['employees', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['services', tenantId] });
      employeesQuery.refetch();
    },
    onError: (error) => {
      console.error('Error creating loginless employee profile:', error);
      setShowErrorDialog(true);
    },
  });

  const handleCreateSuccessDialogClose = () => {
    setOpenCreateSuccessDialog(false);
    navigate(successPath ?? '/admin/manage-employees');
  };

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleScheduleChange = (day: keyof WorkHours, updatedSchedule: BitStringData) => {
    setSchedules((prevSchedules) => ({
      ...prevSchedules,
      [day]: generateScheduleBitString(updatedSchedule),
    }));
  };

  const handleRecurringBreaksChange = (day: keyof WorkHours, breaks: RecurringBreak[]) => {
    setRecurringBreakTemplates((prev) => ({
      ...prev,
      [day]: breaks,
    }));
  };

  const handleProfilePictureChange = (newPicture?: File) => {
    setProfilePictureFile(newPicture);
  };

  const handleCopyLink = async () => {
    if (inviteLink) {
      await navigator.clipboard.writeText(inviteLink);
      setSnackbarMessage('Invite link copied to clipboard!');
      setSnackbarOpen(true);
    }
  };

  const handleShare = async () => {
    if (inviteLink && navigator.share) {
      try {
        await navigator.share({ title: 'Employee Invite', text: 'Join our system using this invite link:', url: inviteLink });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      setSnackbarMessage('Sharing not supported on this device.');
      setSnackbarOpen(true);
    }
  };

  const serviceChips = () => (
    servicesQuery.isLoading ? <LoadingSpinner /> : (servicesQuery.data ?? []).length ? (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={800} letterSpacing=".08em">Services</Typography>
          <Button size="small" onClick={() => setSelectedServices(
            selectedServices.length === (servicesQuery.data ?? []).length ? [] : (servicesQuery.data ?? []).map((service) => service.id))}>
            {selectedServices.length === (servicesQuery.data ?? []).length ? 'Clear' : 'Select all'}
          </Button>
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(servicesQuery.data ?? []).map((service: Service) => {
            const selected = selectedServices.includes(service.id);
            return (
              <Chip key={service.id} label={service.title} clickable aria-pressed={selected}
                color={selected ? 'primary' : 'default'} variant={selected ? 'filled' : 'outlined'}
                onClick={() => handleServiceToggle(service.id)}
                sx={{ height: 38, borderRadius: 999, fontWeight: 700 }} />
            );
          })}
        </Box>
      </Box>
    ) : (
      <Alert severity="info">No services yet. You can assign services later from Services.</Alert>
    )
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Stack spacing={1.5}>
            {[
              { admin: false, title: resourceSingular, detail: 'Sees their own schedule and appointments.' },
              { admin: true, title: `${resourceSingular} with admin access`,
                detail: `Can also manage ${resourcePlural.toLowerCase()}, services and appointments.` },
            ].map((option) => {
              const selected = isAdmin === option.admin;
              return (
                <Box key={String(option.admin)} component="button" type="button" aria-pressed={selected}
                  onClick={() => setIsAdmin(option.admin)}
                  sx={{ textAlign: 'left', p: 2, borderRadius: 3.5, cursor: 'pointer', font: 'inherit',
                    border: '2px solid', borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'rgba(25,118,210,.05)' : 'background.paper' }}>
                  <Typography fontWeight={850}>{option.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{option.detail}</Typography>
                </Box>
              );
            })}
          </Stack>
        );
      case 1:
        return serviceChips();
      case 2:
        return (
          <Stack spacing={2}>
            {isInviteLoading ? <LoadingSpinner /> : inviteLink ? (
              <>
                <TextField label="Invite link" value={inviteLink} InputProps={{ readOnly: true }}
                  onFocus={(event) => event.target.select()} />
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={handleCopyLink} sx={{ fontWeight: 800 }}>Copy link</Button>
                </Stack>
              </>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              Send this link to the person you’re adding. It works once and expires in 2 hours.
            </Typography>
          </Stack>
        );
      default:
        return null;
    }
  }

  if (!tenantId) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Missing tenant ID</Typography>
        </Paper>
      </Container>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Failed to load data
          </Typography>
          <Typography>Please refresh the page or try again later.</Typography>
        </Paper>
      </Container>
    );
  }

  if (isAtLimit) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <QuestionCard eyebrow={resourcePlural} title={`You’ve reached your ${resourceSingular.toLowerCase()} limit`}
          detail={isOrchestratorMode
            ? `Your plan includes ${loginlessEmployeeLimit} ${resourcePlural.toLowerCase()}.`
            : `Your plan includes ${employeeLimit} ${employeeLimit === 1 ? 'login' : 'logins'}.`}>
          <Typography color="text.secondary">
            {isOrchestratorMode
              ? `To add more, contact support@scheduify.com.`
              : `To add more, upgrade your plan from Manage subscription or contact support@scheduify.com.`}
          </Typography>
        </QuestionCard>
      </Box>
    );
  }

  if (isOrchestratorMode) {
    const thing = resourceSingular.toLowerCase();
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <QuestionCard eyebrow={resourcePlural} title={`Add a ${thing}`}>
          <Stack spacing={2.5}>
            {showEmployeeProfilePhoto && (
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={800} letterSpacing=".08em">
                  {isResourceMode ? 'Photo (optional)' : 'Profile photo (optional)'}
                </Typography>
                <ImageSelect
                  key={imageSelectResetKey}
                  onImageChange={handleProfilePictureChange}
                  allowDelete
                  styleType="circle"
                  altText={`${firstName || resourceSingular} ${lastName || ''}`.trim()}
                />
              </Box>
            )}
            {isResourceMode ? (
              <TextField label="Name" value={fullNameValue} placeholder={`e.g. ${resourceSingular} 1`}
                onChange={(event) => {
                  const parsed = parseFullName(event.target.value);
                  setFirstName(parsed.firstName);
                  setLastName(parsed.lastName);
                }} />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth label="First name" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                <TextField fullWidth label="Last name" value={lastName} onChange={(event) => setLastName(event.target.value)} />
              </Stack>
            )}
            {showEmployeeBio && (
              <TextField
                label={isResourceMode ? 'Description (optional)' : 'Bio (optional)'}
                value={bio}
                onChange={(event) => setBio(event.target.value.slice(0, EMPLOYEE_BIO_MAX_LENGTH))}
                placeholder={isResourceMode ? `What customers should know about this ${thing}.` : 'A short introduction for customers.'}
                helperText={`${bio.length}/${EMPLOYEE_BIO_MAX_LENGTH}`}
                multiline
                minRows={3}
                inputProps={{ maxLength: EMPLOYEE_BIO_MAX_LENGTH }}
              />
            )}
            {!isResourceMode && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth label="Email (optional)" value={email} onChange={(event) => setEmail(event.target.value)} />
                <TextField fullWidth label="Phone (optional)" value={phone} onChange={(event) => setPhone(event.target.value)} />
              </Stack>
            )}

            {serviceChips()}

            <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography fontWeight={850}>Availability</Typography>
                  <Typography variant="body2" color="text.secondary">Starts with your business hours.</Typography>
                </Box>
                <Button variant="outlined" onClick={() => setShowScheduleEditor((prev) => !prev)}
                  sx={{ fontWeight: 800, borderRadius: 2.5, flexShrink: 0 }}>
                  {showScheduleEditor ? 'Done' : 'Customize'}
                </Button>
              </Stack>
              <Collapse in={showScheduleEditor}>
                <Box sx={{ mt: 2 }}>
                  {DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => {
                    const parsed = extractTimesFromBitString(schedules[day]);
                    return (
                      <ScheduleCard
                        key={day}
                        day={day}
                        initialWorkHours={parsed}
                        onScheduleChange={handleScheduleChange}
                        recurringBreaks={recurringBreakTemplates[day]}
                        onRecurringBreaksChange={handleRecurringBreaksChange}
                        templateEndDate={templateEndDate}
                      />
                    );
                  })}
                </Box>
              </Collapse>
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
              sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography fontWeight={850}>Available for booking</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isActive ? 'Customers can book it right away.' : 'Hidden from customers until you turn this on.'}
                </Typography>
              </Box>
              <Switch checked={isActive} onChange={() => setIsActive(!isActive)} inputProps={{ 'aria-label': 'Available for booking' }} />
            </Stack>

            <Button
              variant="contained"
              size="large"
              onClick={() => createLoginlessEmployeeMutation.mutate()}
              disabled={createLoginlessEmployeeMutation.isPending}
              sx={{ minHeight: 52, borderRadius: 3, fontWeight: 900 }}
            >
              {createLoginlessEmployeeMutation.isPending ? 'Adding…' : `Add ${thing}`}
            </Button>
          </Stack>
        </QuestionCard>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
        <ErrorDialog
          open={showErrorDialog}
          onClose={() => setShowErrorDialog(false)}
          message={"Something went wrong! Please try again later, or contact support."}
        />
        <Dialog open={openCreateSuccessDialog} onClose={handleCreateSuccessDialogClose} PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle sx={{ fontWeight: 900 }}>{`${resourceSingular} added`}</DialogTitle>
          <DialogContent>
            <Typography>
              {createdEmployeeName
                ? `${createdEmployeeName} is ready${isActive ? ' for bookings' : ''}.`
                : `Your ${thing} is ready.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button variant="contained" onClick={handleCreateSuccessDialogClose} sx={{ borderRadius: 2.5, fontWeight: 800 }}>
              {`Back to ${resourcePlural}`}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  const stepTitles = [
    `What can this ${resourceSingular.toLowerCase()} do?`,
    'Which services do they offer?',
    'Send the invite',
  ];
  return (
    <Box sx={{ px: 2, py: 3 }}>
      <QuestionCard eyebrow={`Invite a ${resourceSingular.toLowerCase()}`} title={stepTitles[currentStep]}
        progress={{ current: currentStep, total: steps.length }}>
        {renderStepContent()}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button onClick={() => setCurrentStep((prev) => prev - 1)} disabled={currentStep === 0}
            sx={{ fontWeight: 800, visibility: currentStep === 0 ? 'hidden' : 'visible' }}>
            Back
          </Button>
          {currentStep < steps.length - 1 ? (
            <Button variant="contained" size="large"
              onClick={() => {
                if (currentStep === 1) {
                  createInviteMutation.mutate();
                }
                setCurrentStep((prev) => prev + 1);
              }}
              sx={{ minHeight: 48, borderRadius: 3, px: 3.5, fontWeight: 900 }}>
              {currentStep === 1 ? 'Create invite' : 'Continue'}
            </Button>
          ) : !isInviteLoading && inviteLink ? (
            <Button variant="contained" size="large" onClick={handleShare}
              sx={{ minHeight: 48, borderRadius: 3, px: 3.5, fontWeight: 900 }}>
              Share invite
            </Button>
          ) : null}
        </Stack>
      </QuestionCard>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      <ErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={"Something went wrong! Please try again later, or contact support."}
      />
    </Box>
  );

};

export default AddEmployeePage;
